import { Box, Grid, Stack, Typography } from "@mui/material";
import React, { memo, useContext, useState } from "react";
import TextInput from "../../../components/textInput";
import { AppContext } from "../../../lib/Context";
import GlobalButton from "./../../../components/button";
import DynamicTable from "./../../../components/dynamicTable/index";
import { handleOpenSnackBar } from "../../../utils/helper";
import Label from "../../../components/label";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CustomCheckBox from "../../../components/checkbox";
import { useLocation } from "react-router-dom";
import { updateContract } from "../../../services/ContractApi";
import AppLoader from "../../../components/loader";

const tableHeader = ["Created At", "Created By", "Note"];

const displayRows = ["note_created_date", "note_createdBy", "text"];

const useStyle = {
  datePickerStyle: {
    width: "100%",
    "&.MuiFormControl-root .MuiInputBase-root": {
      borderRadius: "4px",
      backgroundColor: "#d4ecff87",
    },
  },
};

const Note = ({ type, contractDetails, fetchDetail }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState("");
  const {
    note,
    setNote,
    userData,
    allNotes,
    setAllNotes,
    contractEndDate,
    setContractEndDate,
    followUpDate,
    setFollowUpDate,
    isUpfrontPaymentCollected,
    setIsUpfrontPaymentCollected,
  } = useContext(AppContext);
  const isAdmin = userData?.designation === "Admin" ? true : false;
  const isDetailPage = location.pathname === "/prospect-details";

  const handleAddNotes = async () => {
    if (!value.trim()) {
      return handleOpenSnackBar("error", "Note is required");
    }

    let tempArr = [...allNotes];
    tempArr.push({
      text: value,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: userData?.username,
    });

    if (type === "edit" && contractDetails) {
      try {
        const response = await updateContract(contractDetails?._id, {
          contractNote: tempArr,
        });
        setIsLoading(true);
        if (response?.data?.status === "success") {
          fetchDetail();
          setValue("");
          handleOpenSnackBar("success", "Note added successfully");
        } else {
          handleOpenSnackBar("error", "Failed to add note");
        }
      } catch (error) {
        handleOpenSnackBar("error", "Failed to add note");
      } finally {
        setIsLoading(false);
      }
    } else {
      setAllNotes(tempArr);
      setValue("");
    }
  };

  return (
    <Box my={3}>
      <AppLoader isVisible={isLoading} />

      <Typography variant="h6" color={"text.charcoalGrey"}>
        Add Additional Notes
      </Typography>

      <Stack
        direction={"row"}
        alignItems={"center"}
        mt={1}
        gap={{ xs: 1, md: 2 }}
      >
        <TextInput
          id={"form-note"}
          required
          type={"text"}
          variant={"filled"}
          value={value}
          onChange={(e) => {
            const formattedValue = e.target.value.replace(
              /\b[a-zA-Z]/g,
              (char) => char.toUpperCase()
            );
            setValue(formattedValue);
          }}
          style={{
            "& .MuiInputBase-multiline": {
              padding: "0px",
            },
          }}
          readonly={isDetailPage}
        />
        <GlobalButton
          variant={"custom_gradient"}
          handleClickBtn={handleAddNotes}
          title={"Add"}
          style={{ width: { xs: "auto", sm: "100px", borderRadius: 6 } }}
          disabled={isDetailPage}
        />
      </Stack>

      <Box my={3}>
        <Typography variant="h6" color={"text.charcoalGrey"}>
          Upfront Payment Collected
        </Typography>

        <Stack direction={"row"} alignItems={"center"} gap={3} mt={1}>
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <CustomCheckBox
              isChecked={isUpfrontPaymentCollected === "Yes"}
              handleChecked={() => {
                if (!isDetailPage) {
                  setIsUpfrontPaymentCollected("Yes");
                }
              }}
            />
            <Typography variant="body1" color={"text.davyGrey"}>
              Yes
            </Typography>
          </Stack>
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <CustomCheckBox
              isChecked={isUpfrontPaymentCollected === "No"}
              handleChecked={() => {
                if (!isDetailPage) {
                  setIsUpfrontPaymentCollected("No");
                }
              }}
            />
            <Typography variant="body1" color={"text.davyGrey"}>
              No
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2} my={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <Label title={"Follow Up Date"} />

            <DatePicker
              sx={useStyle.datePickerStyle}
              value={followUpDate}
              disablePast={true}
              onChange={(newValue) => setFollowUpDate(newValue)}
              format="DD.MM.YY"
              readOnly={isDetailPage}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <Label title={"Contract End Date"} />

            <DatePicker
              sx={useStyle.datePickerStyle}
              value={contractEndDate}
              disablePast={true}
              onChange={(newValue) => setContractEndDate(newValue)}
              format="DD.MM.YY"
              readOnly={isDetailPage}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={6}>
            <Label title={"Notes"} />
            <TextInput
              id={"form-note"}
              required
              type={"text"}
              variant={"filled"}
              value={note}
              onChange={(e) => {
                const formattedValue = e.target.value.replace(
                  /\b[a-zA-Z]/g,
                  (char) => char.toUpperCase()
                );
                setNote(formattedValue);
              }}
              style={{
                "& .MuiInputBase-multiline": {
                  padding: "0px",
                },
              }}
              readonly={isDetailPage}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>

      <Typography variant="h6" color={"text.charcoalGrey"} mt={2}>
        All Notes
      </Typography>
      {allNotes?.length > 0 ? (
        <DynamicTable
          tableData={allNotes.slice().reverse()}
          displayRows={displayRows}
          tableHeader={tableHeader}
        />
      ) : (
        <Typography textAlign={"center"} fontWeight={500}>
          No Note Found
        </Typography>
      )}
    </Box>
  );
};

export default memo(Note);
